import DOMPurify from 'dompurify';
import { SYSTEM_EVENT_TYPE, TICKET_STATUS_CHANGE_EVENTS } from './constants';

// ── Email helpers ─────────────────────────────────────────────────────

export const isValidEmail = e => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

export const parseEmailList = (str, validate = true) =>
  (str || '')
    .split(',')
    .map(e => e.trim())
    .filter(e => e && (!validate || isValidEmail(e)));

export const escapeHtml = text =>
  (text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/\n/g, '<br>');

export const sanitizeHtml = html =>
  DOMPurify.sanitize(html, { ADD_ATTR: ['class'], FORBID_TAGS: ['style'] });

// ── File upload ───────────────────────────────────────────────────────

export const FILE_UPLOAD = {
  MAX_SIZE: 10 * 1024 * 1024,
  ALLOWED_TYPES: [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
    'text/plain',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
};

export const getMessageType = mimeType =>
  mimeType?.startsWith('image/') ? 'IMAGE' : 'FILE';

export const formatFileSize = bytes => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const getFileNameFromUrl = url => {
  if (!url) return 'Attachment';
  try {
    const path = new URL(url).pathname;
    const parts = path.split('/');
    return decodeURIComponent(parts[parts.length - 1]) || 'Attachment';
  } catch {
    return 'Attachment';
  }
};

export const getDateLabel = rawTs => {
  if (!rawTs) return null;
  const date = new Date(rawTs);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (isSameDay(date, today)) return 'Today';
  if (isSameDay(date, yesterday)) return 'Yesterday';
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
  });
};

/**
 * Maps a backend message object to the UI format used by ThreadItem.
 * Handles both v1 payload (type/system_event_type/timestamp) and legacy format.
 * If the message already has a `kind` field (optimistic/mock), it passes through unchanged.
 */
export const mapBackendMessage = m => {
  if (m.kind) return m;

  const id = m.id || m.message_id;
  const senderMap = {
    CUSTOMER: 'user',
    customer: 'user',
    user: 'user',
    parent: 'user',
    teacher: 'user',
    AGENT: 'agent',
    agent: 'agent',
    BOT: 'bot',
    bot: 'bot',
    SYSTEM: 'system',
    system: 'system',
  };

  const isNote = m.type === 'NOTE' || m.sender_type === 'note' || m.item_type === 'NOTE';
  const isSystemEvent =
    m.type === 'SYSTEM_EVENT' ||
    m.sender_type === 'system' ||
    m.item_type === 'SYSTEM_MESSAGE';
  // Ticket status transitions render as inline system pills (same UI as
  // CATEGORY_CHANGED), even when published on support_agent_internal.<id>
  // where item_type may be tagged SYSTEM_INTERNAL_MESSAGE.
  const isStatusChangeEvent = TICKET_STATUS_CHANGE_EVENTS.includes(m.system_event_type);
  const isInternalEvent =
    !isStatusChangeEvent &&
    (m.item_type === 'SYSTEM_INTERNAL_MESSAGE' ||
      m.system_event_type === SYSTEM_EVENT_TYPE.BOT_SUMMARY ||
      m.system_event_type === SYSTEM_EVENT_TYPE.FRT_BREACHED ||
      m.system_event_type === SYSTEM_EVENT_TYPE.SLA_WARNING);

  const rawTs = m.timestamp || m.created_at;
  const ts = rawTs
    ? new Date(rawTs).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    : '';

  if (isNote) {
    return {
      id,
      rawTs,
      kind: 'note',
      body: m.content || '',
      author: m.sender_name || '',
      ts,
      messageType: m.message_type || 'TEXT',
      mediaUrl: m.media_url || null,
      emailSubject: m.email_subject || null,
      emailCc: m.email_cc || null,
      emailBcc: m.email_bcc || null,
      emailToList: m.email_to_list || null,
      senderEmail: m.sender_email || null,
    };
  }
  if (isSystemEvent && !isInternalEvent) {
    return {
      id,
      rawTs,
      kind: 'system',
      tint: 'info',
      body: m.content || '',
      title: (m.system_event_type || '').replace(/_/g, ' '),
    };
  }
  if (isInternalEvent) {
    return { id, rawTs, kind: 'event', icon: 'sparks', title: m.content || '', sub: ts };
  }
  const who = senderMap[m.sender_type] || '';
  return {
    id,
    rawTs,
    kind: 'msg',
    who,
    role: m.sender_type || '',
    name: m.sender_name || '',
    body: m.content || '',
    ts,
    messageType: m.message_type || 'TEXT',
    mediaUrl: m.media_url || null,
    // Email metadata (present only for email-sourced ticket messages)
    htmlContent: m.html_content || null,
    emailSubject: m.email_subject || null,
    emailCc: m.email_cc || null,
    emailBcc: m.email_bcc || null,
    emailToList: m.email_to_list || null,
    senderEmail: m.sender_email || null,
    sentBy: m.sent_by || null,
    sentByName: m.sent_by_name || null,
    structuredData: m.structured_data || null,
  };
};
